/* ============================================================
   FitLibrary — fitlibrary-header.js
   Canonical global header behaviour, ported from the approved
   HTML: desktop dropdowns with hover open/close delays, focus /
   Escape / outside-click dismissal, mobile glass menu and
   accordions, breakpoint reset, homepage scroll state.
   ============================================================ */
(function () {
  "use strict";
  var FL = (window.FitLibrary = window.FitLibrary || {});

  function initNav(scope) {
    var nav = (scope || document).querySelector(".nav") || document.querySelector(".nav");
    if (!nav || nav.dataset.flNavReady === "true") return;
    nav.dataset.flNavReady = "true";
    navBehaviour(nav);
    if (nav.getAttribute("data-page") === "home") homeScroll(nav);
    else measure(nav);
  }

  function measure(nav) {
    function m() {
      var overlap = nav.offsetHeight + parseFloat(getComputedStyle(nav).marginTop || "0");
      document.documentElement.style.setProperty("--homepage-nav-overlap", Math.round(overlap) + "px");
      document.documentElement.style.setProperty("--fl-sticky-offset", Math.round(overlap + 6) + "px");
    }
    m();
    window.addEventListener("resize", m);
  }

  /* Homepage: transparent at the top, glass pill after the approved 32px. */
  function homeScroll(nav) {
    measure(nav);
    var SCROLL_TRIGGER = 32;
    var scrolledState = null;
    function setNavState() {
      var s = window.scrollY > SCROLL_TRIGGER;
      if (s !== scrolledState) { scrolledState = s; nav.classList.toggle("is-scrolled", s); }
    }
    var surface = document.querySelector(".home-surface");
    var ticking = false;
    function update() {
      ticking = false;
      setNavState();
      if (!surface || FL.reduceMotion()) return;
      var vh = window.innerHeight;
      /* Ambient scroll range starts as the eBook Library (#library) enters the
         post-hero surface and reaches 1 near the end of the homepage content,
         so the hero is not counted as ambient scroll distance. */
      var startEl = document.getElementById("library") || surface;
      var startAbs = startEl.getBoundingClientRect().top + window.scrollY;
      var surfaceBottomAbs = surface.getBoundingClientRect().bottom + window.scrollY;
      var start = startAbs - vh;
      var end = surfaceBottomAbs - vh;
      var range = end - start;
      var p = range > 0 ? Math.min(1, Math.max(0, (window.scrollY - start) / range)) : 0;
      surface.style.setProperty("--amb-progress", p.toFixed(4));
    }
    window.addEventListener("scroll", function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  function navBehaviour(nav) {
    var OPEN_DELAY = parseInt(nav.getAttribute("data-dd-open-delay"), 10) || 120;
    var CLOSE_DELAY = parseInt(nav.getAttribute("data-dd-close-delay"), 10) || 220;
    var dds = Array.prototype.slice.call(nav.querySelectorAll(".nav-dd"));
    var current = null, openTimer = null, closeTimer = null;

    function closeDD(dd) {
      dd.classList.remove("is-open");
      dd.querySelector(".nav-dd__trigger").setAttribute("aria-expanded", "false");
      if (current === dd) current = null;
    }
    function openDD(dd) {
      clearTimeout(closeTimer); clearTimeout(openTimer);
      if (current && current !== dd) closeDD(current);
      dd.classList.add("is-open");
      dd.querySelector(".nav-dd__trigger").setAttribute("aria-expanded", "true");
      current = dd;
    }
    function closeAll() { dds.forEach(closeDD); }

    dds.forEach(function (dd) {
      var trigger = dd.querySelector(".nav-dd__trigger");
      dd.addEventListener("mouseenter", function () {
        clearTimeout(closeTimer);
        openTimer = setTimeout(function () { openDD(dd); }, OPEN_DELAY);
      });
      dd.addEventListener("mouseleave", function () {
        clearTimeout(openTimer);
        closeTimer = setTimeout(function () { closeDD(dd); }, CLOSE_DELAY);
      });
      trigger.addEventListener("click", function (e) {
        e.preventDefault();
        if (dd.classList.contains("is-open")) closeDD(dd); else openDD(dd);
      });
      trigger.addEventListener("focus", function () {
        try { if (trigger.matches(":focus-visible")) openDD(dd); } catch (err) {}
      });
      dd.addEventListener("focusout", function (e) {
        if (!dd.contains(e.relatedTarget)) closeDD(dd);
      });
    });

    document.addEventListener("click", function (e) {
      if (!e.target.closest(".nav-dd")) closeAll();
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && current) {
        var t = current.querySelector(".nav-dd__trigger");
        closeDD(current); t.focus();
      }
    });
    window.addEventListener("scroll", function () { if (current) closeAll(); }, { passive: true });

    var burger = nav.querySelector(".nav__burger");
    var mobile = nav.querySelector(".nav__mobile");

    /* NO BODY-SCROLL LOCK.
       This used to add `no-scroll` to <body>, i.e. `overflow: hidden`. That
       turns <body> into a scroll container, and `position: sticky` resolves
       against its NEAREST SCROLLPORT — so the sticky header group instantly
       re-scoped to a scrollport that never scrolls and snapped back to its
       static position near the top of the document. Opening the menu (or
       touching an accordion) therefore looked like the page jumping upward.

       The menu does not need a modal-style lock: it is absolutely positioned
       inside the sticky `.nav` and carries its own scroll boundary in CSS
       (`max-height: min(72vh, 560px); overflow-y: auto; overscroll-behavior:
       contain`), so it scrolls internally and never chains to the document.
       `body.no-scroll` itself is left in place for genuine modals. */
    function setMenu(open) {
      nav.classList.toggle("is-menu-open", open);
      if (burger) {
        burger.setAttribute("aria-expanded", open ? "true" : "false");
        burger.setAttribute("aria-label", open ? "Close menu" : "Open menu");
      }
    }
    if (burger) {
      burger.addEventListener("click", function () {
        setMenu(!nav.classList.contains("is-menu-open"));
      });
    }
    if (mobile) {
      /* Accordions change internal menu state ONLY. No scrollIntoView, no
         window.scrollTo, no anchor navigation, no scroll save/restore — the
         heads are real <button> elements and the panel is out of document
         flow, so the document position cannot move. */
      Array.prototype.forEach.call(mobile.querySelectorAll(".m-acc__head"), function (head) {
        head.addEventListener("click", function () {
          var acc = head.closest(".m-acc");
          var willOpen = !acc.classList.contains("is-open");
          Array.prototype.forEach.call(mobile.querySelectorAll(".m-acc.is-open"), function (a) {
            if (a !== acc) {
              a.classList.remove("is-open");
              a.querySelector(".m-acc__head").setAttribute("aria-expanded", "false");
            }
          });
          acc.classList.toggle("is-open", willOpen);
          head.setAttribute("aria-expanded", willOpen ? "true" : "false");
        });
      });
      Array.prototype.forEach.call(mobile.querySelectorAll("a"), function (a) {
        a.addEventListener("click", function () { setMenu(false); });
      });
    }
    document.addEventListener("click", function (e) {
      if (nav.classList.contains("is-menu-open") && !nav.contains(e.target)) setMenu(false);
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && nav.classList.contains("is-menu-open")) {
        setMenu(false);
        if (burger) burger.focus();
      }
    });
    window.addEventListener("resize", function () {
      if (window.innerWidth > 980 && nav.classList.contains("is-menu-open")) setMenu(false);
    });

    /* Dynamic cart count kept in sync with every Ajax cart change. */
    document.addEventListener("fitlibrary:cart:updated", function () {
      if (FL.refreshCartCount) FL.refreshCartCount();
    });
  }

  if (FL.register) FL.register("Header", initNav);
  else document.addEventListener("DOMContentLoaded", function () { initNav(document); });

  document.addEventListener("shopify:section:load", function (e) {
    if (e.target.querySelector && e.target.querySelector(".nav")) {
      e.target.querySelector(".nav").dataset.flNavReady = "";
      initNav(e.target);
    }
  });
})();
